import images from "@/images";
import type { SeriesEvent } from "@/types/series.types";
import { durationBetweenTimestamps } from "@/utils/date-utils";
import { getCreateProjectUrl, niceDate, resizedImage } from "@/utils/utils";
import Icon, { COLORS, ICONS } from "@general-ui/icon";
import Image from "@general-ui/image/image";
import "./project-card.scss";
import { Fragment, useCallback, useMemo, useState } from "react";
import { Popover } from "@general-ui/popover/popover";
import { useLanguageParam } from "@/components/live-event/utils";
import { useThunkDispatch, useTypedSelector } from "@/store/reducers/use-typed-selector";
import { selectCurrentSeries } from "@/store/selectors/series";
import { clearCurrentSeries, deleteEventFromSeries, loadSeriesFull } from "@/store/actions/admin/series";
import { useEventUuidParam } from "@/utils/use-event-uuid-param";
import { useHistory } from "react-router-dom";
import { useGetAdminUrl } from "@/utils/admin-routing-utils";
import { SessionPanelMap } from "../../session-panel-route-map";
import { SessionTabNames } from "@/types/working-model";
import classNames from "classnames";
import { Tooltip } from "@general-ui/tooltip/tooltip";
import { OptionalComponent } from "@/utils/optional-component";

interface Props {
	event: SeriesEvent;
}
export default function ProjectCard({ event }: Props) {
	const eventUuid = useEventUuidParam();
	const [popoverOpen, setPopoverOpen] = useState(false);
	const language = useLanguageParam();
	const currentSeries = useTypedSelector(selectCurrentSeries);
	const dispatch = useThunkDispatch();
	const history = useHistory();
	const adminPath = useGetAdminUrl();

	const removeProject = useCallback(
		(projectUuid: string) => async () => {
			if (currentSeries?.uuid && projectUuid) {
				await dispatch(
					deleteEventFromSeries({
						channel: currentSeries.channel,
						seriesUuid: currentSeries.uuid,
						eventUuid: projectUuid,
					})
				);

				if (projectUuid === eventUuid) {
					dispatch(clearCurrentSeries());
					history.replace(adminPath({
						path: SessionPanelMap[SessionTabNames.Content_v2]
					}));
				} else {
					await dispatch(
						loadSeriesFull({ channel: currentSeries.channel, seriesUuid: currentSeries.uuid })
					);
				}
			}
		},
		[adminPath, currentSeries?.channel, currentSeries?.uuid, dispatch, eventUuid, history]
	);

	const Wrapper = useCallback(({ children }: { children: JSX.Element }) => {
		if (!event.is_published) {
			return (
				<Tooltip tooltip={<span className="C2">This project has never been published<br />and will not yet be visible to attendees.</span>}>
					{children}
				</Tooltip>
			);
		}

		return <Fragment>{children}</Fragment>;
	}, [event.is_published]);

	return (
		<div className="project-row-container">
			<Wrapper>
				<div className={classNames("project-row", { 'not-published': !event.is_published })}>
					<Image
						fallback={event.event_image ?? images.TemplatesFallbackImage}
						src={resizedImage((event.event_image ?? images.TemplatesFallbackImage) as string, {
							height: 40,
							width: 72,
							fit: 'cover',
						})} />
					<div className="title-container">
						<div className="S3">{event.title.base}</div>
						{event.timestamp && (
							<time className="C1">{niceDate(event.timestamp, 'short')}</time>
						)}
					</div>
					{!!(event.timestamp && event.end_timestamp) && (
						<div className="duration">
							<Icon name={ICONS.CLOCK} size={12} color={COLORS.WHITE} />
							<div className="C1">{durationBetweenTimestamps(event.timestamp, event.end_timestamp)}</div>
						</div>
					)}
					<button className="popover-trigger" onClick={() => setPopoverOpen(prev => !prev)}>
						<Icon name={ICONS.THREE_DOTS_VERTICAL} size={12} color={COLORS.WHITE} />
					</button>
				</div>
			</Wrapper>

			<Popover open={popoverOpen} onClose={() => setPopoverOpen(false)}>
				<div className="project-row-popover">
					<button
						onClick={() => (window.location.href = `${getCreateProjectUrl()}/project/edit/${event.uuid}/${language || 'en'}`)}
					>
						<span className="P2">
							View details
						</span>
					</button>
					<button
						onClick={() => window.location.href = `${getCreateProjectUrl()}/project/edit/${event.uuid}/${language || 'en'}/stats`}
					>
						<span className="P2">
							View stats
						</span>
					</button>
					<button
						onClick={removeProject(event.uuid)}
					>
						<span className="P2 delete">
							Remove
						</span>
					</button>
				</div>
			</Popover>
		</div>
	);
}